import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  CardActions,
  Grid,
  Typography,
} from '@mui/material';
import { BadgeStatus } from '../conneqt.status';
import {
  ViewConneqtPayload,
  ConneqtStatus,
} from '../../../state/reduxRepositories/conneqt/conneqt.interfaces';
import { getPlayerDetails } from '../conneqt.helper';
import { getInitials } from '../../../helpers/initialsExtractor';
import { ConneqtedPlayer } from './conneqtedPlayer.dumb';
import { di } from '../../../state/inversify.config';
import { LoginDomainService } from '../../login/loginDomainService';
import { ConneqtPageQueryService } from '../conneqt.queryParam.service';
import { AllConneqtsDomain } from './allConneqts.domain';
import { EmailWithIcon } from '../../../components/emailWithIcon/emailWithIcon';
import { Avatar } from '../../../components/avatar/avatar';
import { Button } from '../../../components/button/button';

interface AllGuardianConneqtsProps {
  pageQueryParam: ConneqtPageQueryService;
}
export function AllGuardianConneqts({
  pageQueryParam,
}: AllGuardianConneqtsProps) {
  const { t } = useTranslation();

  const allConneqtsDomain = di.get<AllConneqtsDomain>(AllConneqtsDomain);
  const conneqts = allConneqtsDomain.useConneqts();
  const players = allConneqtsDomain.usePlayerSelector();

  const loginDomainService = di.get<LoginDomainService>(LoginDomainService);
  const isNetworkConnected = loginDomainService.useNetworkSelector();
  const loggedInUser = loginDomainService.useLoggedInUserSelector();

  const { selectedConneqtId } = pageQueryParam.useSelectMergedQuery();

  useEffect(() => {
    if (!selectedConneqtId) return;

    const element = document.getElementById(selectedConneqtId);
    if (!element) return;

    // 👇 Will scroll smoothly to the top of the next section
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [conneqts, selectedConneqtId]);

  return (
    <Grid container spacing={2}>
      {conneqts?.map((conneqt: ViewConneqtPayload) => (
        <Grid item xs={12} sm={12} md={8} lg={4} key={conneqt.id}>
          <Card sx={{ borderRadius: 4 }}>
            <Typography variant="caption" sx={{ p: 2 }}>
              {t('CONNEQTED_FROM')}:
            </Typography>
            <CardHeader
              avatar={
                <Avatar
                  title={getInitials(
                    getPlayerDetails(conneqt.requestPlayerClues)?.name,
                  )}
                />
              }
              action={
                <BadgeStatus
                  testId={`status-${getPlayerDetails(
                    conneqt.requestPlayerClues,
                  )?.name.replace(/\s/g, '')}`}
                  status={conneqt.status}
                />
              }
              title={conneqt.accountName}
              titleTypographyProps={{ variant: 'subtitle2' }}
              subheaderTypographyProps={{ variant: 'subtitle2' }}
            />
            <Typography variant="subtitle2" sx={{ ml: 3 }}>
              {/* {conneqt.requestUserEmail !== loggedInUser?.email ? (
                <EmailWithIcon email={conneqt.requestUserEmail} />
              ) : (
                <EmailWithIcon email={conneqt.responseEmail} />
              )} */}
              <EmailWithIcon email={conneqt.requestUserEmail} />
            </Typography>
            <CardContent>
              <Divider sx={{ mb: 1 }} />
              {players && (
                <>
                  <Typography variant="caption">
                    {t('CONNEQTED_TO')}:
                  </Typography>
                  <ConneqtedPlayer
                    player={players.find(
                      (player) =>
                        player.id === conneqt.responsePlayerId ||
                        player.id === conneqt.requestPlayerId,
                    )}
                  />
                </>
              )}
            </CardContent>
            <CardActions>
              {conneqt.status === ConneqtStatus.Conneqted &&
                isNetworkConnected && (
                  <Button
                    testId="action-disconneqt"
                    type="submit"
                    variant="contained"
                    fullWidth
                    color="secondary"
                    onClick={() =>
                      allConneqtsDomain.toggleConneqtStatus(
                        conneqt,
                        loggedInUser,
                      )
                    }
                    label={t('DISCONNEQT')}
                  />
                )}
              {conneqt.status === ConneqtStatus.Disconneqted &&
                loggedInUser?.accountId === conneqt.disconnectedAccountId &&
                isNetworkConnected && (
                  <Button
                    testId="action-conneqt"
                    type="submit"
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={() =>
                      allConneqtsDomain.toggleConneqtStatus(conneqt)
                    }
                    label={t('CONNEQT')}
                  />
                )}
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
